/* eslint-disable react/require-default-props */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import Modal from '../../../components/Modal/Modal';
import { DangerButton, GhostButton } from '../../../styles/ui/Button';
import { FormGrid } from '../../../styles/layout/FormGrid';
import { Field, RequiredMark } from '../../../styles/forms/Field';

// ---------------------------------------------------------------------------
// Cancelamento de cobrança com MOTIVO OBRIGATÓRIO.
//
// Nasceu de dor real: boletos apareciam cancelados sem justificativa e, um mês
// depois, ninguém lembrava o porquê. O backend passou a exigir `cancel_reason`
// (mín. 8 caracteres após trim) no DELETE da cobrança e do grupo.
//
// Compartilhado entre a ficha da cobrança e a do parcelamento — a regra dos 8
// caracteres mora AQUI, num lugar só, em vez de replicada em cada modal.
//
// `extraAction` cobre o caso da ficha: quando a cobrança pertence a um
// parcelamento, o operador escolhe entre cancelar só ela ou o grupo inteiro —
// com o MESMO motivo, que o backend propaga a todas as parcelas.
// ---------------------------------------------------------------------------

const MIN_REASON_LENGTH = 8;

const Description = styled.p`
   margin: 0 0 14px;
   color: #555;
   font-size: 14px;
   line-height: 1.55;
`;

const FieldError = styled.span`
   font-size: 11px;
   color: ${({ theme }) => theme?.colors?.danger ?? '#b71c1c'};
   margin-top: 2px;
`;

export default function CancelReasonModal({
   isOpen,
   onClose,
   onConfirm,
   title = 'Cancelar cobrança',
   description,
   confirmLabel = 'Cancelar cobrança',
   extraAction = null,
   isBusy = false,
}) {
   const [reason, setReason] = useState('');
   const [error, setError] = useState(null);

   useEffect(() => {
      if (isOpen) {
         setReason('');
         setError(null);
      }
   }, [isOpen]);

   // Valida no clique (em vez de desabilitar o botão): assim o operador lê o
   // motivo da recusa, em vez de encarar um botão morto sem explicação.
   const withValidReason = (handler) => () => {
      const trimmed = reason.trim();
      if (trimmed.length < MIN_REASON_LENGTH) {
         setError(
            `Descreva o motivo com pelo menos ${MIN_REASON_LENGTH} caracteres.`,
         );
         return;
      }
      setError(null);
      handler(trimmed);
   };

   const footer = (
      <>
         <GhostButton type="button" onClick={onClose} disabled={isBusy}>
            Voltar
         </GhostButton>
         <DangerButton
            type="button"
            onClick={withValidReason(onConfirm)}
            disabled={isBusy}
         >
            {confirmLabel}
         </DangerButton>
         {extraAction && (
            <DangerButton
               type="button"
               onClick={withValidReason(extraAction.onConfirm)}
               disabled={isBusy}
            >
               {extraAction.label}
            </DangerButton>
         )}
      </>
   );

   return (
      <Modal
         isOpen={isOpen}
         onClose={onClose}
         title={title}
         maxWidth="480px"
         footer={footer}
      >
         {description && <Description>{description}</Description>}

         <FormGrid>
            <Field className="col-12">
               <label htmlFor="cancel-reason">
                  Motivo do cancelamento
                  <RequiredMark>*</RequiredMark>
               </label>
               <textarea
                  id="cancel-reason"
                  maxLength={255}
                  placeholder="Ex: cliente desistiu da compra, boleto emitido em duplicidade..."
                  value={reason}
                  onChange={(e) => {
                     setReason(e.target.value);
                     setError(null);
                  }}
                  disabled={isBusy}
                  aria-invalid={!!error || undefined}
               />
               {error && <FieldError>{error}</FieldError>}
            </Field>
         </FormGrid>
      </Modal>
   );
}

CancelReasonModal.propTypes = {
   isOpen: PropTypes.bool.isRequired,
   onClose: PropTypes.func.isRequired,
   onConfirm: PropTypes.func.isRequired,
   title: PropTypes.string,
   description: PropTypes.string,
   confirmLabel: PropTypes.string,
   extraAction: PropTypes.shape({
      label: PropTypes.string.isRequired,
      onConfirm: PropTypes.func.isRequired,
   }),
   isBusy: PropTypes.bool,
};
